import { EPaymentPlan } from '@if/core/types'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { CustomPromoPlanOption, PromoPlan } from '@src/data/types/PromoPlan'

// A null wire expiresOn means the plan never expires; consumers expect a Date, so pin one far out.
const DISTANT_FUTURE = '2099-12-31T23:59:59.999Z'

// The single multi-term plan a Custom PromoPlan merchant offers → the toggle's option model.
export const toCustomPromoPlanOption = (plan: PromoPlan | null | undefined): CustomPromoPlanOption | null =>
  plan
    ? { id: plan.id, label: plan.label ?? '', termRates: plan.termRates.map((r) => ({ termMonths: r.termMonths })) }
    : null

// A single-term applicable plan → the legacy MerchantPaymentPlan shape. Wire rates are coefficients;
// MerchantPaymentPlan rates are percent, so ×100 (matching the old backend ResolvedPlanMapper.ToPercent).
export const toMerchantPaymentPlan = (plan: PromoPlan): MerchantPaymentPlan => {
  if (plan.termRates.length !== 1)
    throw new Error(`toMerchantPaymentPlan: plan ${plan.id} must have exactly one term rate, got ${plan.termRates.length}`)
  const term = plan.termRates[0]
  return {
    id: plan.id as unknown as EPaymentPlan, // GUID carried in the EPaymentPlan slot (existing cast pattern)
    interestRate: term.regularRate * 100,
    reducedInterestRate: term.promoRate * 100,
    reducedRateDurationInMonths: term.promoDurationMonths ?? 0,
    loanTerm: term.termMonths,
    merchantFeeRate: plan.merchantFeeRate * 100,
    activeFrom: new Date(plan.activeFrom),
    expiresOn: new Date(plan.expiresOn ?? DISTANT_FUTURE),
    promoCode: plan.label ?? '',
    supportsChargeback: plan.supportsChargeback,
    borrowerFeeRate: plan.borrowerFeeRate * 100,
  }
}

export const toMerchantPaymentPlans = (plans: PromoPlan[]): MerchantPaymentPlan[] =>
  plans.filter((p) => p.termRates.length > 0).map(toMerchantPaymentPlan)
